import React, { useState, useCallback, memo } from "react";
import PropTypes from 'prop-types';
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/Logo.webp";
import "../styles/Navbar.css";
import ContactModel from "./ContactModel";

const NavLink = memo(({ to, children, className = "", onClick }) => (
  <Link to={to} className={`text-decoration-none mx-3 ${className}`} onClick={onClick}>
    {children}
  </Link>
));

const ScrollButton = memo(({ sectionId, children, onClick, className = "" }) => (
  <button 
    onClick={() => onClick(sectionId)} 
    className={`text-decoration-none mx-3 btn btn-link p-0 ${className}`}
  >
    {children}
  </button>
));

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const openModal = useCallback(() => setShowModal(true), []);
  const handleCloseModal = useCallback(() => setShowModal(false), []);

  const handleScrollToSection = useCallback((sectionId) => {
    closeMenu();

    if (window.location.pathname === "/") {
      const section = document.getElementById(sectionId);
      const y = section?.getBoundingClientRect().top + window.pageYOffset - 80;
      y && window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: sectionId } });
    }
  }, [closeMenu, navigate]);

  return (
    <>
      <header className="sticky-header py-3">
        <div className="container position-relative">
          <div className="d-flex justify-content-between align-items-center position-relative">
            {/* Logo */}
            <Link className="navbar-brand" to="/" onClick={closeMenu}>
              <img src={logo} alt="logo" className="img-fluid" width="180" />
            </Link>

            {/* Desktop Navigation */}
            <div className="d-none d-md-block position-absolute top-50 start-50 translate-middle align-items-center">
              <nav className="d-flex align-items-center navigation-desk me-4">
                <ScrollButton sectionId="advantage" onClick={handleScrollToSection}>
                  Why Flexi Store
                </ScrollButton>
                <ScrollButton sectionId="features" onClick={handleScrollToSection}>
                  Features
                </ScrollButton>
                <ScrollButton sectionId="case" onClick={handleScrollToSection}>
                  Case Study
                </ScrollButton>
                <NavLink to="/about">About Us</NavLink>
                <NavLink to="/contact-us">Contact Us</NavLink>
              </nav>
            </div>

            {/* Desktop CTA Button */}
            <div className="d-none d-md-block">
              <button
                className="btn btn-primary btn-hover"
                onClick={openModal}
              >
                Book a Free Strategy Call
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="d-md-none ms-auto">
              <button
                className="navbar-toggler border-0 bg-transparent p-2"
                type="button"
                onClick={toggleMenu}
                aria-label="Toggle navigation"
                aria-expanded={isMenuOpen}
              >
                <i className={`fa-solid ${isMenuOpen ? 'fa-times' : 'fa-bars'} fs-4`} />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`d-md-none mobile-menu ${isMenuOpen ? 'mobile-menu-show' : ''}`}>
            <nav className="d-flex flex-column py-3 mobile-nav">
              <div className="container">
                <div className="d-flex flex-column gap-2">
                  <button 
                    onClick={() => handleScrollToSection("advantage")} 
                    className="text-decoration-none text-start w-100 p-3 mobile-nav-link btn btn-link"
                  >
                    Why Flexi Store
                  </button>
                  <button 
                    onClick={() => handleScrollToSection("features")} 
                    className="text-decoration-none text-start w-100 p-3 mobile-nav-link btn btn-link"
                  >
                    Features
                  </button>
                  <button 
                    onClick={() => handleScrollToSection("case")} 
                    className="text-decoration-none text-start w-100 p-3 mobile-nav-link btn btn-link"
                  >
                    Case Study
                  </button>
                  <Link 
                    to="/about" 
                    className="text-decoration-none text-start w-100 p-3 mobile-nav-link btn-link" 
                    onClick={closeMenu}
                  >
                    About Us
                  </Link>
                  <Link 
                    to="/contact-us" 
                    className="text-decoration-none text-start w-100 p-3 mobile-nav-link btn-link" 
                    onClick={closeMenu}
                  >
                    Contact Us
                  </Link>
                </div>
                
                <div className="mt-3 px-3">
                  <button
                    className="btn btn-primary btn-hover w-100 py-2"
                    onClick={() => {
                      closeMenu();
                      openModal();
                    }}
                  >
                    Book a Free Strategy Call
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      <ContactModel show={showModal} setShow={setShowModal} />
    </>
  );
}

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func
};

ScrollButton.propTypes = {
  sectionId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default memo(Navbar);
